import {
  React,
  Box,
  Typography,
  useNavigate,
  useSelector,
  useDispatch,
  Autocomplete,
  TextField,
  CircularProgress,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '../../utils/globalImports';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../store';
import { ExtendedAccountResponse } from '../../store/types';
import { useGetAllCustomersQuery } from '../../api/customerApiSlice';
import { useGetClientsByCustomerIdQuery } from '../../api/clientApiSlice';
import { CustomerResponse } from '../../pages/customer/types';
import { Client } from '../../pages/client/types';
import { setLakehouseSelection } from '../../store/slices/vaultilake';

interface VaultilakeScopeSelectorPopupProps {
  open: boolean;
  onClose: () => void;
}

const VaultilakeScopeSelectorPopup: React.FC<VaultilakeScopeSelectorPopupProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const auth = useSelector((state: RootState) => state.auth);
  const account = auth.account as ExtendedAccountResponse | null;
  const role = account?.user?.role || account?.role;
  const loggedInCustomerId = account?.user?.customer_id || account?.customer_id || '';

  const { selectedCustomerId: customerIdFromRedux, selectedClientId: clientIdFromRedux } = useSelector(
    (state: RootState) => state.vaultilake,
  );

  const [selectedCustomerId, setSelectedCustomerId] = React.useState<string | null>(null);
  const [selectedClientId, setSelectedClientId] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) {
      const initialCustomerId = role === 'CustomerAdmin' ? loggedInCustomerId : customerIdFromRedux;
      setSelectedCustomerId(initialCustomerId);
      if (initialCustomerId === customerIdFromRedux) {
        setSelectedClientId(clientIdFromRedux);
      } else {
        setSelectedClientId(null);
      }
    }
  }, [open, customerIdFromRedux, clientIdFromRedux, role, loggedInCustomerId]);

  const { data: customers, isLoading: isLoadingCustomers } = useGetAllCustomersQuery(undefined, {
    skip: role !== 'SuperAdmin',
  });

  const { data: clients, isLoading: isLoadingClients } = useGetClientsByCustomerIdQuery(selectedCustomerId, {
    skip: !selectedCustomerId,
  });

  const handleContinue = () => {
    dispatch(setLakehouseSelection({ customerId: selectedCustomerId, clientId: selectedClientId }));
    navigate('/data-platform');
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('vaultilake.lakehouse.vaultilakeScopeSelectorPopup.selectLakehouseScope')}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          {role === 'SuperAdmin' && (
            <Autocomplete<CustomerResponse>
              id="customer-select-popup"
              size="small"
              options={customers || []}
              autoHighlight
              getOptionLabel={(option) => option?.name || ''}
              value={customers?.find((c: CustomerResponse) => c.customer_id === selectedCustomerId) || null}
              isOptionEqualToValue={(option, value) => option.customer_id === value.customer_id}
              onChange={(_, newValue) => {
                setSelectedCustomerId(newValue?.customer_id || null);
                setSelectedClientId(null);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('vaultilake.lakehouse.vaultilakeScopeSelectorPopup.customer')}
                  placeholder={t('vaultilake.lakehouse.vaultilakeScopeSelectorPopup.searchCustomer')}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoadingCustomers ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          )}
          {(role === 'SuperAdmin' || role === 'CustomerAdmin') && (
            <Autocomplete<Client>
              id="client-select-popup"
              size="small"
              options={clients || []}
              autoHighlight
              disabled={!selectedCustomerId || isLoadingClients}
              getOptionLabel={(option) => option?.name || ''}
              value={clients?.find((c: Client) => c.client_id === selectedClientId) || null}
              isOptionEqualToValue={(option, value) => option.client_id === value.client_id}
              onChange={(_, newValue) => {
                setSelectedClientId(newValue?.client_id || null);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('vaultilake.lakehouse.vaultilakeScopeSelectorPopup.client')}
                  placeholder={t('vaultilake.lakehouse.vaultilakeScopeSelectorPopup.searchClient')}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoadingClients ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined" color="primary">
          {t('vaultilake.buttons.cancel')}
        </Button>
        <Button onClick={handleContinue} disabled={!selectedClientId} variant="contained" color="primary">
          {t('vaultilake.buttons.continue')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VaultilakeScopeSelectorPopup;
